/**
 * Create Role Modal Component
 * Allows creating new roles with permission selection
 */

import React, { useState } from 'react';
import {
  Modal,
  Form,
  Input,
  Collapse,
  Space,
  Typography,
  Row,
  Col,
  Divider,
  Tag,
  Checkbox,
} from 'antd';
import { FaShieldAlt, FaFolderOpen } from 'react-icons/fa';
import { Permission } from '@/types/permissions.types';
import { PermissionCategories } from '@/features/settings/constants/permissions';

const { Text } = Typography;
const { Panel } = Collapse;

interface CreateRoleModalProps {
  open: boolean;
  loading?: boolean;
  onCancel: () => void;
  onSubmit: (data: {
    roleName: string;
    isDefaultRole: boolean;
    permissions: Permission[];
    permissionsJson: string;
  }) => void;
}

export const CreateRoleModal: React.FC<CreateRoleModalProps> = ({
  open,
  loading = false,
  onCancel,
  onSubmit,
}) => {
  const [form] = Form.useForm();
  const [selectedPermissions, setSelectedPermissions] = useState<Permission[]>([]);

  const handlePermissionToggle = (permission: Permission, checked: boolean) => {
    setSelectedPermissions((prev) => {
      if (checked) {
        return [...prev, permission];
      } else {
        return prev.filter((p) => p !== permission);
      }
    });
  };

  const handleCategoryToggle = (categoryPermissions: Permission[], checked: boolean) => {
    setSelectedPermissions((prev) => {
      if (checked) {
        // Add all permissions from this category
        const newPermissions = [...prev];
        categoryPermissions.forEach((perm) => {
          if (!newPermissions.includes(perm)) {
            newPermissions.push(perm);
          }
        });
        return newPermissions;
      } else {
        // Remove all permissions from this category
        return prev.filter((p) => !categoryPermissions.includes(p));
      }
    });
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // Generate permissions JSON - NEW FLAT STRUCTURE: { "permission": true }
      const permissionsJson: Record<string, boolean> = {};

      // Add all permissions with boolean values
      PermissionCategories.forEach((category) => {
        category.permissions.forEach((perm) => {
          permissionsJson[perm.id] = selectedPermissions.includes(perm.id);
        });
      });

      onSubmit({
        roleName: values.roleName,
        isDefaultRole: true, // Always true by default
        permissions: selectedPermissions,
        permissionsJson: JSON.stringify(permissionsJson),
      });

      // Reset form
      form.resetFields();
      setSelectedPermissions([]);
    } catch (error) {
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setSelectedPermissions([]);
    onCancel();
  };

  const isCategoryFullySelected = (categoryPermissions: Permission[]): boolean => {
    return categoryPermissions.every((perm) => selectedPermissions.includes(perm));
  };

  const isCategoryPartiallySelected = (categoryPermissions: Permission[]): boolean => {
    const selected = categoryPermissions.filter((perm) => selectedPermissions.includes(perm));
    return selected.length > 0 && selected.length < categoryPermissions.length;
  };

  return (
    <Modal
      title={
        <Space>
          <FaShieldAlt style={{ color: '#1890ff' }} />
          <span>Create New Role</span>
        </Space>
      }
      open={open}
      onCancel={handleCancel}
      onOk={handleSubmit}
      okText="Create Role"
      cancelText="Cancel"
      confirmLoading={loading}
      width={800}
      bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
    >
      <Form form={form} layout="vertical" autoComplete="off">
        <Form.Item
          name="roleName"
          label="Role Name"
          rules={[
            { required: true, message: 'Please enter a role name' },
            { min: 3, message: 'Role name must be at least 3 characters' },
            { max: 100, message: 'Role name must not exceed 100 characters' },
          ]}
        >
          <Input placeholder="e.g., Custom Sales Manager" size="large" />
        </Form.Item>

        <Divider>Permissions ({selectedPermissions.length} selected)</Divider>

        <Collapse
          accordion={false}
          defaultActiveKey={PermissionCategories.map((cat) => cat.id)}
          style={{ background: '#fafafa' }}
        >
          {PermissionCategories.map((category) => {
            const categoryPermissionIds = category.permissions.map((p) => p.id);
            const isFullySelected = isCategoryFullySelected(categoryPermissionIds);
            const isPartiallySelected = isCategoryPartiallySelected(categoryPermissionIds);
            const selectedCount = categoryPermissionIds.filter((p) =>
              selectedPermissions.includes(p)
            ).length;

            return (
              <Panel
                key={category.id}
                header={
                  <div
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Space>
                      <Checkbox
                        checked={isFullySelected}
                        indeterminate={isPartiallySelected}
                        onChange={(e) =>
                          handleCategoryToggle(categoryPermissionIds, e.target.checked)
                        }
                        onClick={(e) => e.stopPropagation()}
                      />
                      <FaFolderOpen style={{ color: '#8b1538' }} />
                      <Text strong>{category.name}</Text>
                    </Space>
                    <Tag color={selectedCount > 0 ? 'blue' : 'default'}>
                      {selectedCount}/{category.permissions.length}
                    </Tag>
                  </div>
                }
              >
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  {category.permissions.map((perm) => (
                    <Row key={perm.id} gutter={8}>
                      <Col span={24}>
                        <Checkbox
                          checked={selectedPermissions.includes(perm.id)}
                          onChange={(e) => handlePermissionToggle(perm.id, e.target.checked)}
                        >
                          <Space direction="vertical" size={0}>
                            <Text strong style={{ fontSize: 13 }}>
                              {perm.label}
                            </Text>
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              {perm.description}
                            </Text>
                          </Space>
                        </Checkbox>
                      </Col>
                    </Row>
                  ))}
                </Space>
              </Panel>
            );
          })}
        </Collapse>
      </Form>
    </Modal>
  );
};

export default CreateRoleModal;
